import React from "react";
import {
  Instagram,
  Sparkles,
  ExternalLink,
  Film,
  Play,
  Heart,
  Share2,
} from "lucide-react";
import {
  socialReels,
  instagramHandle,
  instagramUrl,
  instagramReelsUrl,
} from "../data/social";
import { businessInfo } from "../data/business";

export default function Social() {
  return (
    <div className="space-y-10 sm:space-y-14 pb-16">
      {/* 🍨 INSTAGRAM HUB HEADER */}
      <section className="bg-gradient-to-b from-pink-50/80 via-rose-50/40 to-transparent pt-8 sm:pt-14 pb-8 border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-pink-200 text-pink-800 text-xs sm:text-sm font-bold shadow-xs">
            <Instagram className="w-4 h-4 text-pink-600" />
            <span>Official Instagram Reels Hub</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#2C3E50] tracking-tight">
            Fresh Reels from @{instagramHandle}
          </h1>

          <p className="text-sm sm:text-base text-stone-600 max-w-xl mx-auto leading-relaxed">
            Watch our official reels below — from real fruit preparation and ice cream churning to parlor moments in Udaipur.
          </p>

          {/* ACTION BUTTONS */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <a
              href={instagramReelsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 hover:opacity-95 text-white font-bold text-xs sm:text-sm px-6 py-3 rounded-full shadow-lg shadow-pink-500/20 hover:scale-105 active:scale-95 transition-all"
            >
              <Film className="w-4 h-4" />
              <span>Watch on Instagram App</span>
              <ExternalLink className="w-3.5 h-3.5 opacity-80" />
            </a>

            <a
              href={instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-white hover:bg-stone-50 text-[#2C3E50] border border-stone-200 font-bold text-xs sm:text-sm px-5 py-3 rounded-full shadow-xs hover:scale-105 active:scale-95 transition-all"
            >
              <Instagram className="w-4 h-4 text-pink-600" />
              <span>Follow @{instagramHandle}</span>
            </a>
          </div>
        </div>
      </section>

      {/* 🎬 REELS EMBED GRID (NUMBER-WISE 1 TO 6) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 pb-2 border-b border-stone-200/80">
          <div className="flex items-center gap-2">
            <Film className="w-5 h-5 text-pink-600" />
            <h2 className="text-lg sm:text-xl font-bold text-[#2C3E50]">
              Featured Instagram Reels ({socialReels.length})
            </h2>
          </div>
          <span className="text-xs font-semibold text-stone-500">
            Ordered 1 to 6
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {socialReels.map((reel) => (
            <div
              key={reel.id}
              className="bg-white rounded-3xl overflow-hidden border border-stone-200/80 shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
            >
              {/* Header Badge */}
              <div className="p-3.5 bg-stone-50/80 border-b border-stone-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-pink-500 text-white font-extrabold text-xs flex items-center justify-center shadow-xs">
                    {reel.number}
                  </span>
                  <span className="font-bold text-xs text-[#2C3E50]">
                    @{instagramHandle}
                  </span>
                </div>

                <a
                  href={reel.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] font-bold text-pink-600 hover:text-pink-700 flex items-center gap-1"
                >
                  <span>Reel #{reel.number}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              {/* REAL INSTAGRAM EMBED IFRAME */}
              <div className="p-2 sm:p-3 bg-white flex justify-center items-center">
                <iframe
                  src={reel.embedUrl}
                  title={`HimCream Reel ${reel.number}`}
                  className="w-full h-[480px] sm:h-[500px] rounded-2xl border-0 bg-stone-50 shadow-inner"
                  scrolling="no"
                  allowTransparency="true"
                  allow="encrypted-media"
                  loading="lazy"
                />
              </div>

              {/* Card Footer Button */}
              <div className="p-3.5 bg-stone-50/80 border-t border-stone-100 flex items-center justify-between">
                <span className="text-xs text-stone-500 font-medium">
                  {reel.title}
                </span>

                <a
                  href={reel.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 bg-pink-500 hover:bg-pink-600 active:scale-95 text-white text-xs font-bold px-3.5 py-1.5 rounded-full shadow-xs transition"
                >
                  <Play className="w-3 h-3 fill-current" />
                  <span>Open Reel</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 🌟 CALLOUT FOOTER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-3xl p-8 sm:p-12 text-white text-center space-y-4 shadow-xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-pink-300 text-xs font-bold">
            <Instagram className="w-4 h-4 text-pink-400" />
            <span>@{instagramHandle} on Instagram</span>
          </div>

          <h3 className="text-2xl sm:text-3xl font-extrabold">
            Follow Us for Daily Udaipur Foodie Updates
          </h3>

          <p className="text-stone-300 text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
            Tag <strong className="text-white">@himcream.naturals</strong> with hashtag <strong className="text-white">#HimCreamNaturals</strong> when you visit our Udaipur café!
          </p>

          <div className="pt-2 flex flex-wrap justify-center gap-3">
            <a
              href={instagramReelsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold text-sm px-6 py-3 rounded-full shadow-lg transition"
            >
              <Film className="w-4 h-4" />
              <span>Explore All Reels</span>
            </a>

            <a
              href={instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-6 py-3 rounded-full transition"
            >
              <Instagram className="w-4 h-4" />
              <span>Follow Profile</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
